import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Page,
  Layout,
  Card,
  Form,
  FormLayout,
  TextField,
  Select,
  Button,
  SkeletonDisplayText,
  Banner, Checkbox,
  SkeletonBodyText,
  TextStyle, OptionList,
} from "@shopify/polaris";
import Switch from "react-switch";
import PhoneInput from "react-phone-input-2";
import { ShopContext } from "../App";
import axios from "../axios";
import _ from "lodash";
import "react-phone-input-2/lib/style.css";
import { validateWhatsAppNumber } from "./dashboard";
import WhatsAppNumberForm from "./../components/WhatsAppNumber";
import Br from "../components/Br";

const SettingsPage = () => {
  const config = useContext(ShopContext);
  const [viewingPreview, setViewingPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [chatActive, setChatActive] = useState("no");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [widgetPosition, setWidgetPosition] = useState("bottomRight");
  const [widgetText, setWidgetText] = useState("");
  const [widgetType, setWidgetType] = useState("iconOnly");
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [whatsAppNumberError, setWhatsAppNumberError] = useState(false);
  const [iconTextError, setIconTextError] = useState(false);
  const [languageCode, setLanguageCode] = useState("en_US");
  const handleSelectChange = (value) => setLanguageCode(value);
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [acceptOptInForAll, setAcceptOptInForAll] = useState(true);
  const [financialStatus, setFinancialStatus] = useState(['authorized', 'partially_paid', 'paid']);
  const handleOptInChange = useCallback((newChecked) => setAcceptOptInForAll(newChecked), []);

  useEffect(() => {
    axios
      .get(`/dashboard/store-settings/`, {
        headers: {
          shopOrigin: config.shopOrigin,
          xShopHost: config.host,
        },
      })
      .then((response) => {
        if (response.success) {
          setWhatsAppNumber(response.data.whatsapp_number);
          setLanguageCode(response.data.language);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  }, []);

  /**
   * Save the  ap settings
   **/
  const saveSettings = () => {
    setSaving(true);
    // Validations
    if (!validateWhatsAppNumber(whatsAppNumber)) {
      setWhatsAppNumberError(true);
      setSaving(false);
      return false;
    }

    if (chatActive === "yes" && !validateWhatsAppNumber(phoneNumber)) {
      setPhoneNumberError(true);
      setSaving(false);
      return false;
    }

    if (
      (chatActive === "yes" &&
        widgetType === "iconWithText" &&
        widgetText === "") ||
      !widgetText.match(/^[ A-Za-z0-9_+-]*$/)
    ) {
      setIconTextError(true);
      setSaving(false);
      return false;
    }

    axios
      .post(
        `/settings/save`,
        JSON.stringify({
          app_settings: {
            chatActive: chatActive,
            phoneNumber: phoneNumber,
            widgetPosition: widgetPosition,
            widgetType: widgetType,
            widgetText: widgetText,
            acceptOptInForAll: acceptOptInForAll,
            confirmedMessageOnFinancialStatus: financialStatus,
          },
          whatsapp_number: whatsAppNumber,
          language_code: languageCode,
        }),
        {
          headers: {
            shopOrigin: config.shopOrigin,
            xShopHost: config.host,
          },
        }
      )
      .then((response) => {
        setSaving(false);
        if (response.success) {
          setShowMessage(true);
          setShowErrorMessage(false);
        } else {
          setShowErrorMessage(true);
          setShowMessage(false);
        }
        setIconTextError(false);
      })
      .catch((error) => {
        console.log(error);
        setSaving(false);
      });
  };

  let whatsappChatActive = chatActive === "yes";
  useEffect(() => {
    setLoading(true);
    axios
      .get(`/settings`, {
        headers: {
          shopOrigin: config.shopOrigin,
          xShopHost: config.host,
        },
      })
      .then((response) => {
        let appSettings = response.data;
        let chatActive = _.get(appSettings, "chatActive", "");
        setChatActive(_.get(appSettings, "chatActive", "no"));
        setPhoneNumber(_.get(appSettings, "phoneNumber", ""));
        setWidgetPosition(_.get(appSettings, "widgetPosition", "bottomRight"));
        setWidgetText(_.get(appSettings, "widgetText", ""));
        setWidgetType(_.get(appSettings, "widgetType", "iconOnly"));
        setFinancialStatus(_.get(appSettings, "confirmedMessageOnFinancialStatus", ['authorized', 'partially_paid', 'paid']));
        let chatActiveOptions = ["yes", "no"];
        if (chatActiveOptions.includes(chatActive)) {
          setAcceptOptInForAll(_.get(appSettings, "acceptOptInForAll", false));
        } else {
          setAcceptOptInForAll(_.get(appSettings, "acceptOptInForAll", true));
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [config]);

  /**
   * remove the script from the body
   * @param id
   */
  let _removeScript = (id) => {
    let script = document.getElementById(id);
    if (script) {
      script.parentNode.removeChild(script);
    }
    let preview = document.getElementById("whatsapp-chat-widget-container");
    if (preview) {
      preview.parentNode.removeChild(preview);
    }
  };

  /**
   * append the script to the body
   * @param url
   * @param id
   */
  let _addScript = useCallback((url, id) => {
    _removeScript(id);
    let script = document.createElement("script");
    script.setAttribute("id", id);
    script.setAttribute("src", url);
    document.body.appendChild(script);
  }, []);

  let _viewPreview = useCallback(() => {
    const scriptUrl = new URL(process.env.REACT_APP_PUBLIC_JS_URL);
    scriptUrl.searchParams.append("phone", phoneNumber);
    scriptUrl.searchParams.append("position", widgetPosition);
    scriptUrl.searchParams.append(
      "label",
      widgetType === "iconWithText" ? widgetText : ""
    );
    let url = scriptUrl.href;
    if (viewingPreview) {
      _addScript(url, "shopify_whatsapp_preview_js");
    } else {
      _removeScript("shopify_whatsapp_preview_js");
    }
  }, [
    _addScript,
    phoneNumber,
    viewingPreview,
    widgetPosition,
    widgetText,
    widgetType,
  ]);

  useEffect(() => {
    _viewPreview();
  }, [
    phoneNumber,
    widgetPosition,
    widgetType,
    widgetText,
    viewingPreview,
    _viewPreview,
  ]);

  // Export the checkout users as a CSV file
  const exportCheckOutUsersInfo = () => {
    axios
      .get(`/settings/store-export`, {
        headers: {
          shopOrigin: config.shopOrigin,
          xShopHost: config.host,
        },
      })
      .then((response) => {
        let csvDataArray = [];
        const responseData = response.split("\n");
        for (var index = 0; index < responseData.length; index++) {
          var csvData = responseData[index].replace(/(^,)|(,$)/g, "");
          csvDataArray.push([csvData]);
        }
        let csvContent =
          "data:text/csv;charset=utf-8," +
          csvDataArray.map((row) => row.join(",")).join("\n");
        window.open(csvContent);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <Page
      fullWidth
      title="Settings"
      // primaryAction={{
      //   content: "Export CSV",
      //   onClick: exportCheckOutUsersInfo,
      // }}
    >
      <Form onSubmit={saveSettings}>
        <FormLayout>
          <Layout>
            <Layout.AnnotatedSection
              title="Whatsapp chat"
              description="Configure whatsapp by filling the details"
            >
              <Card sectioned>
                {showMessage && (
                  <Banner
                    title="Settings saved successfully!"
                    status="success"
                    onDismiss={() => {
                      setShowMessage(false);
                    }}
                  />
                )}
                {showErrorMessage && (
                  <Banner
                    title="Failed to update!"
                    status="critical"
                    onDismiss={() => {
                      setShowErrorMessage(false);
                    }}
                  />
                )}
                <br />

                {loading ? (
                  <>
                    <SkeletonDisplayText size="medium" />
                    <br /> <SkeletonBodyText />
                  </>
                ) : (
                  <>
                    <div className="Polaris-SettingAction">
                      <div className="Polaris-SettingAction__Setting">
                        <TextStyle variation="strong">
                          Enable WhatsApp chat on your store?
                        </TextStyle>
                      </div>
                      <div className="Polaris-SettingAction__Action">
                        <Switch
                          onChange={(value) => {
                            setChatActive(value ? "yes" : "no");
                          }}
                          checked={whatsappChatActive}
                        />
                      </div>
                    </div>
                  </>
                )}
                <Br height={20} />
                {whatsappChatActive && (
                  <>
                    <div className="">
                      <div className="Polaris-Labelled__LabelWrapper">
                        <div className="Polaris-Label">
                          <label className="Polaris-Label__Text">
                            <TextStyle variation="subdued">
                              Phone number (Used for Whatsapp chat)
                              <span className="important">*</span>
                            </TextStyle>
                            {phoneNumberError ? (
                              <span
                                style={{ marginLeft: "20px", color: "red" }}
                              >
                                Please enter the valid phone number
                              </span>
                            ) : (
                              ""
                            )}
                          </label>
                        </div>
                      </div>
                      <PhoneInput
                        autoFormat={false}
                        countryCodeEditable={false}
                        enableSearch={true}
                        containerStyle={{ zIndex: "999" }}
                        inputStyle={{ width: "100%", borderColor: "#c4cdd5" }}
                        country={"us"}
                        value={phoneNumber}
                        onChange={(value) => {
                          setPhoneNumber(value);
                          setPhoneNumberError(false);
                        }}
                      />
                    </div>
                    <Br height={20} />
                    <Select
                      label={
                        <TextStyle variation="subdued">Widget type</TextStyle>
                      }
                      options={[
                        { label: "Icon only", value: "iconOnly" },
                        { label: "Icon and text", value: "iconWithText" },
                      ]}
                      onChange={(val) => {
                        setWidgetType(val);
                      }}
                      value={widgetType}
                    />
                    <Br height={20} />
                    {widgetType === "iconWithText" && (
                      <>
                        <TextField
                          label={
                            <TextStyle variation="subdued">
                              Chat widget text{" "}
                              {iconTextError ? (
                                <span
                                  style={{ marginLeft: "20px", color: "red" }}
                                >
                                  * Please enter the valid text
                                </span>
                              ) : (
                                ""
                              )}
                            </TextStyle>
                          }
                          value={widgetText}
                          onChange={(val) => {
                            setWidgetText(val);
                          }}
                        />
                        <Br height={20} />
                      </>
                    )}

                    <Select
                      label={
                        <TextStyle variation="subdued">
                          Widget position
                        </TextStyle>
                      }
                      options={[
                        { label: "Bottom right", value: "bottomRight" },
                        { label: "Bottom left", value: "bottomLeft" },
                        { label: "Centered right", value: "centeredRight" },
                        { label: "Centered left", value: "centeredLeft" },
                      ]}
                      onChange={(val) => {
                        setWidgetPosition(val);
                      }}
                      value={widgetPosition}
                    />
                    <Br height={20} />
                  </>
                )}
                {loading ? (
                  <SkeletonDisplayText size="medium" />
                ) : (
                  <div>
                    {chatActive === "yes" ? (
                      <Button
                        onClick={() => {
                          setViewingPreview(!viewingPreview);
                        }}
                      >
                        {viewingPreview ? "Close preview" : "View preview"}
                      </Button>
                    ) : (
                      ""
                    )}
                  </div>
                )}
              </Card>
            </Layout.AnnotatedSection>
          </Layout>
          <Br height={25} />
          <Layout>
            <Layout.AnnotatedSection
              title="General Settings"
              description="Update your WhatsApp number & Language"
            >
              <Card sectioned title="GENERAL SETTINGS">
                {loading ? (
                  <>
                    <SkeletonDisplayText size="medium" />
                    <br /> <SkeletonBodyText />
                  </>
                ) : (
                  <>
                    <WhatsAppNumberForm
                      phoneNumberError={whatsAppNumberError}
                      whatsAppNumber={whatsAppNumber}
                      change={(value) => {
                        setWhatsAppNumber(value);
                        setWhatsAppNumberError(false);
                      }}
                      selectChange={handleSelectChange}
                      languageCode={languageCode}
                      saving={saving}
                      isHomePage={false}
                    />
                  </>
                )}
              </Card>
            </Layout.AnnotatedSection>
          </Layout>
          <Br height={25} />
          <Layout>
            <Layout.AnnotatedSection
                title="Opt-in control"
                description={<>
                  By default, only customers who opt-in at the checkout will be considered for messaging. But if you would like to send messages to all the customers (Example: order confirmation message is transactional), you can enable this field.</>
            }
            >
              <Card sectioned title="">
                {loading ? (
                    <>
                      <SkeletonDisplayText size="medium" />
                      <br /> <SkeletonBodyText />
                    </>
                ) : (
                    <>
                      <FormLayout>
                        <Checkbox
                            label="Auto opt-in valid phone numbers at Checkout"
                            checked={acceptOptInForAll}
                            onChange={handleOptInChange}
                        />
                      </FormLayout>
                    </>
                )}
              </Card>
            </Layout.AnnotatedSection>
          </Layout>
          <Br height={25} />
          <Layout>
            <Layout.AnnotatedSection
                title="Order statuses for sending order confirmation message"
                description={<>
                  By default, order confirmation message can be triggered when the order is successful with statuses Authorized, Paid, Partially paid. If you need, you can select / unselect any of the order statuses.</>
                }
            >
              <Card sectioned title="">
                {loading ? (
                    <>
                      <SkeletonDisplayText size="medium" />
                      <br /> <SkeletonBodyText />
                    </>
                ) : (
                    <>
                      <FormLayout>
                        <OptionList
                            title="Financial status"
                            onChange={setFinancialStatus}
                            options={[
                              {value: 'authorized', label: 'Authorized'},
                              {value: 'paid', label: 'Paid'},
                              {value: 'partially_paid', label: 'Partially Paid'},
                              {value: 'pending', label: 'Pending'},
                            ]}
                            selected={financialStatus}
                            allowMultiple
                        />
                      </FormLayout>
                    </>
                )}
              </Card>
            </Layout.AnnotatedSection>
          </Layout>
          <Br height={5} />
          <div style={{ marginLeft: "36%" }}>
            <Button submit loading={saving} primary>
              Save
            </Button>{" "}
          </div>
          <Br height={25} />
        </FormLayout>
      </Form>
    </Page>
  );
};
export default SettingsPage;
